
import React, { useState, useEffect } from "react";
import {
  Send,
  PhoneCall,
  Mail,
  CheckCircle,
  FileText,
  AlertCircle,
  Upload,
  User,
  ArrowRight,
  Sparkles,
  Shield,
  Leaf,
  Award,
  Clock,
  Star,
  Heart,
  Globe,
  Zap,
  Target,
  Eye,
  Truck,
  Package,
  CheckCircle2,
  XCircle,
  Info,
  Plus,
  Minus,
  Palette,
  Image,
  FileImage,
  Download,
  RefreshCw
} from "lucide-react";
import { useCartContext } from "../context/CartContext";
import axios from "axios";

const DevisPage = () => {
  const [activeStep, setActiveStep] = useState(1);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [validationErrors, setValidationErrors] = useState({});
  const [sizesQuantities, setSizesQuantities] = useState({});
  const [sizes, setSizes] = useState([]);
  const [products, setProducts] = useState([]);
  const [dataLoading, setDataLoading] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [focusedField, setFocusedField] = useState("");

  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: "",
    interest: "",
    quantity: "",
    sizes: [],
    message: "",
    price: "",
    logoFile: null,
    logoPreview: null,
    isLogo: false,
    colorOptions: {
      productColors: ["#ffffff"], // Always at least one color
    },
    colorRequirement: "single",
  });

  // Add color function
  const addColor = () => {
    setFormData((prev) => ({
      ...prev,
      colorOptions: {
        ...prev.colorOptions,
        productColors: [...(prev.colorOptions?.productColors || []), "#000000"],
      },
    }));
  };

  const updateColor = (index, color) => {
    // Check that productColors exists
    if (!formData.colorOptions?.productColors) {
      return;
    }

    const updatedColors = [...formData.colorOptions.productColors];
    updatedColors[index] = color;

    setFormData((prev) => ({
      ...prev,
      colorOptions: {
        ...prev.colorOptions,
        productColors: updatedColors,
      },
    }));
  };

  const removeColor = (index) => {
    // Check that productColors exists
    if (!formData.colorOptions?.productColors) {
      return;
    }

    // Don't remove if it's the last color
    if (formData.colorOptions.productColors.length <= 1) {
      return;
    }

    const updatedColors = formData.colorOptions.productColors.filter(
      (_, i) => i !== index
    );

    setFormData((prev) => ({
      ...prev,
      colorOptions: {
        ...prev.colorOptions,
        productColors: updatedColors,
      },
    }));
  };

  // Render color inputs function
  const renderColorInputs = () => {
    // Check that productColors exists and is an array
    const productColors = formData.colorOptions?.productColors || [];

    return (
      <div className="space-y-4 ">
        <h4 className="font-medium text-gray-700">Product Colors</h4>
        {productColors.map((color, index) => (
          <div key={index} className="flex items-center space-x-2">
            <input
              type="color"
              value={color}
              onChange={(e) => updateColor(index, e.target.value)}
              className="w-20 h-10 rounded-lg border border-4 border-black"
            />
            <span className="text-sm text-gray-500">{color}</span>
            <button
              type="button"
              onClick={() => removeColor(index)}
              className="text-red-500 hover:bg-red-50 p-2 rounded"
              disabled={productColors.length <= 1}
            >
              Remove
            </button>
          </div>
        ))}
        {/* <button
          type="button"
          onClick={addColor}
          className="mt-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
        >
          + Add Product Color
        </button> */}
      </div>
    );
  };

  const handleSizeQuantityChange = (size, value) => {
    const numericValue = parseInt(value) || 0;
    setSizesQuantities((prev) => ({
      ...prev,
      [size]: numericValue
    }));
  };

  const handleLogoUpload = (e) => {
    const file = e.target.files[0];

    // Validate file type and size
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/gif",
      "application/pdf",
    ];
    const maxSize = 5 * 1024 * 1024; // 5MB

    if (file) {
      if (!allowedTypes.includes(file.type)) {
        setValidationErrors((prev) => ({
          ...prev,
          logoFile: ["Only JPG, PNG, GIF and PDF formats are allowed"],
        }));
        return;
      }

      if (file.size > maxSize) {
        setValidationErrors((prev) => ({
          ...prev,
          logoFile: ["File size must not exceed 5 MB"],
        }));
        return;
      }

      // Create preview for image files
      const reader = new FileReader();
      reader.onloadend = () => {
        setFormData((prev) => ({
          ...prev,
          logoFile: file,
          logoPreview: reader.result,
        }));
      };
      reader.readAsDataURL(file);

      // Clear any previous logo file errors
      if (validationErrors.logoFile) {
        const { logoFile, ...restErrors } = validationErrors;
        setValidationErrors(restErrors);
      }
    }
  };

  const removeLogoFile = () => {
    setFormData((prev) => ({
      ...prev,
      logoFile: null,
      logoPreview: null,
    }));
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;

    // Special handling for logo checkbox
    if (name === "isLogo") {
      setFormData((prev) => ({
        ...prev,
        [name]: checked,
        // Reset logo-related fields if unchecked
        logoFile: checked ? prev.logoFile : null,
        logoPreview: checked ? prev.logoPreview : null,
      }));
    } else {
      // Existing input handling
      const inputValue = type === "checkbox" ? checked : sanitizeInput(value);

      setFormData((prev) => ({
        ...prev,
        [name]: inputValue,
      }));
    }

    // Clear validation error when field is modified
    if (validationErrors[name]) {
      setValidationErrors((prev) => ({
        ...prev,
        [name]: "",
      }));
    }
  };

  // Get cart context
  const { cartItems, totalPrice, selectedProductForDevis } = useCartContext();

  // Helper function to format price by removing trailing zeros
  const formatPrice = (price) => {
    // Convert to string first
    const priceStr = price.toString();
    
    // If it's not a valid number, return as is
    if (isNaN(parseFloat(priceStr))) {
      return priceStr;
    }
    
    // Parse as number to remove trailing zeros, then convert back to string
    const numPrice = parseFloat(priceStr);
    
    // If it's a whole number, add .00
    if (numPrice % 1 === 0) {
      return numPrice.toString() + '.00';
    }
    
    // For decimal numbers, parseFloat automatically removes trailing zeros
    return numPrice.toString();
  };

  // Animation trigger
  useEffect(() => {
    setIsVisible(true);
  }, []);

  // Fetch sizes and products data
  useEffect(() => {
    const fetchData = async () => {
      try {
        setDataLoading(true);
        
        // Fetch sizes and products in parallel
        const [sizesResponse, productsResponse] = await Promise.all([
          axios.get("https://api.greenecobagsusa.com/api/sizes"),
          axios.get("https://api.greenecobagsusa.com/api/products"),
        ]);

        setSizes(Array.isArray(sizesResponse.data) ? sizesResponse.data : []);

        // Handle different response formats for products
        let productsData = [];
        if (productsResponse.data && productsResponse.data.data && Array.isArray(productsResponse.data.data)) {
          // Format { data: [...] }
          productsData = productsResponse.data.data;
        } else if (Array.isArray(productsResponse.data)) {
          // Format direct array
          productsData = productsResponse.data;
        } else if (productsResponse.data && typeof productsResponse.data === 'object') {
          // Format object with values to extract
          productsData = Object.values(productsResponse.data);
        }

        setProducts(productsData);
        setError("");
      } catch (err) {
        console.error("Error loading data:", err);
        setError("Unable to load sizes and products. Please try again later.");
      } finally {
        setDataLoading(false);
      }
    };

    fetchData();
  }, []);

  // Effect to pre-fill form with cart data
  useEffect(() => {
    const savedProduct = localStorage.getItem("devisProduct");
    const productInfo =
      selectedProductForDevis ||
      (savedProduct ? JSON.parse(savedProduct) : null);

    if (productInfo) {
      setFormData((prev) => ({
        ...prev,
        interest: productInfo.name || "",
        quantity: productInfo.quantity?.toString() || "",
        price: productInfo.price?.toString() || "",
        // Extract all unique sizes from cart items
        sizes: productInfo.sizes || [],
      }));
    }
  }, [selectedProductForDevis, totalPrice, cartItems]);

  // Helper function to sanitize input
  const sanitizeInput = (input) => {
    // Remove potentially harmful HTML/JavaScript tags
    if (typeof input === "string") {
      return input.replace(/<(|\/|[^>/])(|.*?)>/gi, "");
    }
    return input;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError("");
    setValidationErrors({});

    // Debug
    console.log("Form data being submitted:", formData);

    // Validate form
    const errors = validateForm(formData);
    if (Object.keys(errors).length > 0) {
      console.log("Validation errors:", errors);
      setValidationErrors(errors);
      setLoading(false);
      return;
    }

    // Create FormData for file upload
    const formDataToSubmit = new FormData();

    // Append text fields
    formDataToSubmit.append("name", formData.name);
    formDataToSubmit.append("email", formData.email);
    formDataToSubmit.append("phone", formData.phone);
    formDataToSubmit.append("message", formData.message);
    formDataToSubmit.append("quantity", calculateTotalQuantity());
    formDataToSubmit.append("sizes", JSON.stringify(formData.sizes));
    formDataToSubmit.append('sizesQuantities', JSON.stringify(sizesQuantities));

    const sizesWithQuantities = {};
    formData.sizes.forEach(sizeName => {
      sizesWithQuantities[sizeName] = sizesQuantities[sizeName] || 0;
    });
    // formDataToSubmit.append('sizesQuantities', JSON.stringify(sizesWithQuantities));

    // Explicitly send "true" or "false" as string
    // formDataToSubmit.append("isLogo", formData.isLogo ? "true" : "false");
    formDataToSubmit.append("isLogo", formData.isLogo ? 1 : 0);

    formDataToSubmit.append("colorRequirement", formData.colorRequirement);

    // Append color options
    formDataToSubmit.append(
      "productColors",
      JSON.stringify(formData.colorOptions.productColors)
    );

    // Append logo file if exists
    if (formData.isLogo && formData.logoFile) {
      formDataToSubmit.append("logo", formData.logoFile);
    }

    // Display formDataToSubmit content for debugging (optional)
    for (let pair of formDataToSubmit.entries()) {
      console.log(pair[0] + ": " + pair[1]);
    }

    try {
      const response = await fetch("https://api.greenecobagsusa.com/api/quotes", {
        method: "POST",
        body: formDataToSubmit,
      });

      const data = await response.json();
      console.log("Response data:", data);

      if (!response.ok) {
        // Check if we have validation errors in the response
        if (response.status === 422 && data.errors) {
          console.log("Validation errors from server:", data.errors);
          // Set validation errors from the server
          setValidationErrors(data.errors);
          throw new Error("Validation errors were found");
        } else {
          throw new Error(
            data.message || "An error occurred while sending"
          );
        }
      }

      // Handle successful response
      setActiveStep(2);
    } catch (error) {
      console.error("Form submission error:", error);
      setError(error.message);
    } finally {
      setLoading(false);
    }
  };

  // Form validation function - updated
  // Form validation function - updated with improved phone validation
  function validateForm(formData) {
    const errors = {};

    if (!formData.name?.trim()) {
      errors.name = ["Name is required"];
    }

    if (!formData.email?.trim()) {
      errors.email = ["Email is required"];
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      errors.email = ["Email is not valid"];
    }

    if (!formData.phone?.trim()) {
      errors.phone = ["Phone is required"];
    } else {
      // Improved phone validation to accept US format like +1 (720) 362-9369
      const phoneRegex = /^(\+1\s?)?(\(?\d{3}\)?[\s.-]?)?\d{3}[\s.-]?\d{4}$/;
      // Alternative more flexible regex that handles various US formats:
      // const phoneRegex = /^(\+?1[-.\s]?)?\(?([0-9]{3})\)?[-.\s]?([0-9]{3})[-.\s]?([0-9]{4})$/;

      if (!phoneRegex.test(formData.phone.trim())) {
        errors.phone = ["Phone number is not valid. Please use format: +1 (720) 362-9369 or 7203629369"];
      }
    }

    // Size validation
    const selectedSizes = formData.sizes || [];
    if (!Array.isArray(selectedSizes) || selectedSizes.length === 0) {
      errors.sizes = ["Please select at least one size"];
    } else {
      // Check that each selected size has at least 10,000 units
      const sizeErrors = [];
      selectedSizes.forEach(sizeName => {
        const quantity = sizesQuantities[sizeName] || 0;
        if (quantity < 10000) {
          sizeErrors.push(`${sizeName}: minimum 10,000 units required`);
        }
      });

      if (sizeErrors.length > 0) {
        errors.sizesQuantities = sizeErrors;
      }
    }

    if (!formData.message?.trim()) {
      errors.message = ["Message is required"];
    }

    // Product colors validation
    const productColors = formData.colorOptions?.productColors || [];
    if (!Array.isArray(productColors) || productColors.length === 0) {
      errors.productColors = ["Please select at least one color for the product"];
    }

    // If logo is checked but no file is selected
    if (formData.isLogo && !formData.logoFile) {
      errors.logoFile = ["Please upload a logo file"];
    }

    return errors;
  }

  const renderLogoUpload = () => {
    return (
      <div className="space-y-4">
        <h4 className="font-medium text-gray-700">Upload Logo</h4>
        <div className="flex items-center space-x-4">
          <input
            type="file"
            id="logoUpload"
            accept=".jpg,.jpeg,.png,.gif,.pdf"
            onChange={handleLogoUpload}
            className="hidden"
          />
          <label
            htmlFor="logoUpload"
            className="flex items-center space-x-2 px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600 cursor-pointer"
          >
            <Upload className="w-5 h-5" />
            <span>Upload</span>
          </label>
          {formData.logoFile && (
            <button
              type="button"
              onClick={removeLogoFile}
              className="text-red-500 hover:bg-red-50 p-2 rounded"
            >
              Remove
            </button>
          )}
        </div>
        {validationErrors.logoFile && (
          <p className="text-red-500 text-sm mt-1">
            {validationErrors.logoFile[0]}
          </p>
        )}
        {formData.logoPreview && (
          <div className="mt-2">
            <p className="text-sm font-medium text-gray-700 mb-1">Preview:</p>
            <img
              src={formData.logoPreview}
              alt="Logo preview"
              className="h-24 object-contain border rounded-md"
            />
          </div>
        )}
        <p className="text-xs text-gray-500">
          Accepted formats: JPG, PNG (max 5 MB)
        </p>
      </div>
    );
  };

  const processSteps = [
    {
      icon: <FileText className="w-8 h-8" />,
      title: "Fill Out",
      desc: "Detail your needs",
    },
    {
      icon: <Mail className="w-8 h-8" />,
      title: "Send",
      desc: "Submit your request",
    },
    {
      icon: <PhoneCall className="w-8 h-8" />,
      title: "Discuss",
      desc: "We will contact you",
    },
    {
      icon: <CheckCircle className="w-8 h-8" />,
      title: "Confirm",
      desc: "Confirm your quote",
    },
  ];


  const calculateTotalQuantity = () => {
    return Object.values(sizesQuantities).reduce((total, qty) => total + (qty || 0), 0);
  };


  // Get size options for the dropdown
  const getSizeOptions = () => {
    const selectedSizes = formData.sizes || [];

    // Calculate total quantities
    const totalQuantity = calculateTotalQuantity();

    // Show loading state
    if (dataLoading) {
    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Sizes and Quantities</span>
          <span className="text-sm font-medium text-gray-700">
            Total: {totalQuantity.toLocaleString()}
          </span>
        </div>
          <div className="flex justify-center items-center py-8">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
            <span className="ml-2 text-gray-600">Loading sizes...</span>
          </div>
        </div>
      );
    }

    // Show error state
    if (error) {
      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Sizes and Quantities</span>
            <span className="text-sm font-medium text-gray-700">
              Total: {totalQuantity.toLocaleString()}
            </span>
          </div>
          <div className="text-red-500 text-sm p-4 bg-red-50 rounded-lg">
            {error}
          </div>
        </div>
      );
    }

    // Show no sizes available
    if (!Array.isArray(sizes) || sizes.length === 0) {
      return (
        <div className="space-y-4">
          <div className="flex justify-between items-center mb-2">
            <span className="text-sm font-medium text-gray-700">Sizes and Quantities</span>
            <span className="text-sm font-medium text-gray-700">
              Total: {totalQuantity.toLocaleString()}
            </span>
          </div>
          <div className="text-gray-500 text-sm p-4 bg-gray-50 rounded-lg">
            No sizes available
          </div>
        </div>
      );
    }

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center mb-2">
          <span className="text-sm font-medium text-gray-700">Sizes and Quantities</span>
          <span className="text-sm font-medium text-gray-700">
            Total: {totalQuantity.toLocaleString()}
          </span>
        </div>

        {sizes.map((size) => (
          <div key={size.id} className="space-y-2">
            <div className="flex items-center">
              <input
                type="checkbox"
                id={size.id}
                name="sizes"
                value={size.name}
                checked={selectedSizes.includes(size.name)}
                onChange={(e) => {
                  const isChecked = e.target.checked;
                  setFormData((prev) => ({
                    ...prev,
                    sizes: isChecked
                      ? [...(prev.sizes || []), size.name]
                      : (prev.sizes || []).filter((s) => s !== size.name),
                  }));

                  // Clear quantity if size is unchecked
                  if (!isChecked) {
                    setSizesQuantities((prev) => {
                      const newQuantities = { ...prev };
                      delete newQuantities[size.name];
                      return newQuantities;
                    });
                  }
                }}
                className="h-4 w-4 text-green-600 focus:ring-green-500 border-gray-300 rounded"
              />
              <label htmlFor={size.id} className="ml-2 block text-sm text-gray-700">
                {size.label} ({size.name}) - ${formatPrice(size.price)}
              </label>
            </div>

            {/* Show quantity input if size is selected */}
            {selectedSizes.includes(size.name) && (
              <div className="ml-6 mt-2">
                <div className="space-y-1">
                  <input
                    type="number"
                    min="10000"
                    placeholder={`Minimum 10,000 for ${size.name}`}
                    value={sizesQuantities[size.name] || ""}
                    onChange={(e) => {
                      const value = parseInt(e.target.value) || 0;
                      handleSizeQuantityChange(size.name, value);
                    }}
                    className={`px-2 py-1 w-48 border ${(sizesQuantities[size.name] || 0) < 10000 && sizesQuantities[size.name] !== ""
                      ? "border-red-300"
                      : "border-green-300"
                      } rounded text-sm`}
                  />
                  {(sizesQuantities[size.name] || 0) < 10000 && sizesQuantities[size.name] !== "" && (
                    <p className="text-xs text-red-500 ml-1">
                      Min: 10,000
                    </p>
                  )}
                </div>
              </div>
            )}
          </div>
        ))}

        {validationErrors.sizes && (
          <p className="text-red-500 text-sm mt-1">
            {validationErrors.sizes[0]}
          </p>
        )}

        {validationErrors.sizesQuantities && (
          <div className="text-red-500 text-sm mt-1">
            {Array.isArray(validationErrors.sizesQuantities)
              ? validationErrors.sizesQuantities.map((error, index) => (
                <p key={index}>{error}</p>
              ))
              : validationErrors.sizesQuantities[0]}
          </div>
        )}
      </div>
    );
  };



  return (
    <div className="min-h-screen bg-gradient-to-br from-green-50 via-emerald-50 to-teal-50 relative overflow-hidden">
      {/* Background Elements */}
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-20 left-10 w-32 h-32 bg-green-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse"></div>
        <div className="absolute bottom-20 right-10 w-40 h-40 bg-emerald-200 rounded-full mix-blend-multiply filter blur-xl opacity-20 animate-pulse animation-delay-2000"></div>
        <div className="absolute top-1/2 left-1/2 transform -translate-x-1/2 -translate-y-1/2 w-24 h-24 bg-teal-200 rounded-full mix-blend-multiply filter blur-xl opacity-15 animate-pulse animation-delay-4000"></div>
      </div>

      {/* Hero Section */}
      <div className={`transition-all duration-1000 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 text-white py-24 relative overflow-hidden`}>
        {/* Hero Background Elements */}
        <div className="absolute inset-0">
          <div className="absolute top-10 left-10 w-20 h-20 bg-white/10 rounded-full animate-pulse"></div>
          <div className="absolute bottom-10 right-10 w-16 h-16 bg-white/10 rounded-full animate-pulse animation-delay-2000"></div>
          <div className="absolute top-1/2 right-1/4 w-12 h-12 bg-white/10 rounded-full animate-pulse animation-delay-4000"></div>
        </div>
        
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <div className="text-center">
            <div className="inline-flex items-center space-x-2 bg-white/20 backdrop-blur-lg text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <FileText className="w-4 h-4" />
              <span>Get Your Custom Quote</span>
            </div>
            <h1 className="text-6xl font-bold mb-6 text-transparent bg-clip-text bg-gradient-to-r from-white via-green-100 to-emerald-200">
            Quote Request
          </h1>
            <p className="text-xl max-w-3xl mx-auto text-green-100 mb-8">
              Let us guide you through your eco-friendly packaging project with a personalized quote
            </p>
            <div className="flex items-center justify-center space-x-6 text-green-100">
              <div className="flex items-center space-x-2">
                <Shield className="w-5 h-5" />
                <span className="text-sm">Secure Process</span>
              </div>
              <div className="flex items-center space-x-2">
                <Leaf className="w-5 h-5" />
                <span className="text-sm">Eco-Friendly</span>
              </div>
              <div className="flex items-center space-x-2">
                <Award className="w-5 h-5" />
                <span className="text-sm">Quality Guaranteed</span>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Main Content */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-lg flex items-center space-x-3">
            <AlertCircle className="h-5 w-5 text-red-500" />
            <p className="text-red-700">{error}</p>
          </div>
        )}

        {/* Process Steps */}
        <div className={`transition-all duration-1000 delay-200 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} mb-20`}>
          <div className="text-center mb-12">
            <h2 className="text-4xl font-bold text-gray-900 mb-4">How It Works</h2>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto">
              Our simple 4-step process makes getting your custom quote easy and efficient
            </p>
          </div>
          
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
          {processSteps.map((step, index) => (
              <div key={index} className="group">
                <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8 hover:shadow-2xl transition-all duration-300 group-hover:scale-105 text-center relative overflow-hidden">
                  {/* Step Number */}
                  <div className="absolute top-4 right-4 w-8 h-8 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center text-white text-sm font-bold">
                    {index + 1}
                  </div>
                  
                  {/* Icon */}
                  <div className="w-20 h-20 bg-gradient-to-r from-green-500 to-emerald-600 rounded-2xl flex items-center justify-center mx-auto mb-6 group-hover:scale-110 transition-transform duration-300">
                    <div className="text-white">
                    {step.icon}
                  </div>
                </div>
                  
                  {/* Content */}
                  <h3 className="text-xl font-bold text-gray-900 mb-3">
                  {step.title}
                </h3>
                  <p className="text-gray-600 mb-4">{step.desc}</p>
                  
                  {/* Arrow for next step */}
                  {index < processSteps.length - 1 && (
                    <div className="hidden lg:block absolute -right-4 top-1/2 transform -translate-y-1/2">
                      <ArrowRight className="w-6 h-6 text-green-400" />
                    </div>
                  )}
              </div>
            </div>
          ))}
          </div>
        </div>

        {/* Progress Stepper */}
        <div className={`transition-all duration-1000 delay-400 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} max-w-4xl mx-auto mb-16`}>
          <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 p-8">
            <div className="text-center mb-8">
              <h3 className="text-2xl font-bold text-gray-900 mb-2">Progress</h3>
              <p className="text-gray-600">Track your quote request status</p>
            </div>
            
            <div className="flex items-center justify-between">
            {[1, 2, 3].map((step) => (
              <React.Fragment key={step}>
                  <div className="flex flex-col items-center group">
                  <div
                      className={`w-16 h-16 rounded-full flex items-center justify-center transition-all duration-500 group-hover:scale-110
                    ${activeStep >= step
                          ? "bg-gradient-to-r from-green-600 to-emerald-600 text-white shadow-lg"
                        : "bg-white text-gray-400 border-2 border-gray-200"
                      }`}
                  >
                      {activeStep > step ? (
                        <CheckCircle2 className="w-8 h-8" />
                      ) : (
                        <span className="text-xl font-bold">{step}</span>
                      )}
                  </div>
                    <span className="text-sm mt-3 font-medium text-gray-700">
                    {step === 1
                      ? "Submission"
                      : step === 2
                        ? "Processing"
                        : "Confirmation"}
                  </span>
                </div>
                {step < 3 && (
                    <div className="flex-1 h-2 mx-4">
                    <div
                      className={`h-full rounded-full transition-all duration-500 
                        ${activeStep > step ? "bg-gradient-to-r from-green-500 to-emerald-500" : "bg-gray-200"}`}
                    ></div>
                  </div>
                )}
              </React.Fragment>
            ))}
            </div>
          </div>
        </div>

        {/* Form or Success Message */}
        {activeStep === 1 ? (
          <div className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} max-w-4xl mx-auto`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 py-8 px-8">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <FileText className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white">
                  Request Form
                </h2>
                    <p className="text-green-100 mt-1">
                      All fields are required for accurate pricing
                </p>
                  </div>
                </div>
              </div>

              <form onSubmit={handleSubmit} className="p-8">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                      <User className="w-4 h-4" />
                      <span>Full Name</span>
                    </label>
                    <div className="relative">
                    <input
                      type="text"
                      name="name"
                      value={formData.name}
                      onChange={handleChange}
                        onFocus={() => setFocusedField('name')}
                        onBlur={() => setFocusedField('')}
                        className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 transition-all duration-300 ${
                          validationErrors.name
                            ? "border-red-300 focus:border-red-500 focus:ring-red-200 bg-red-50"
                            : focusedField === 'name'
                            ? "border-green-500 focus:border-green-500 focus:ring-green-200 bg-green-50"
                            : "border-gray-200 focus:border-green-500 focus:ring-green-200 bg-white"
                        }`}
                        placeholder="Enter your full name"
                      />
                      <User className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${
                        focusedField === 'name' ? 'text-green-500' : 'text-gray-400'
                      }`} />
                    </div>
                    {validationErrors.name && (
                      <div className="flex items-center space-x-2 text-red-500 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span>{validationErrors.name[0]}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                      <Mail className="w-4 h-4" />
                      <span>Email Address</span>
                    </label>
                    <div className="relative">
                    <input
                      type="email"
                      name="email"
                      value={formData.email}
                      onChange={handleChange}
                        onFocus={() => setFocusedField('email')}
                        onBlur={() => setFocusedField('')}
                        className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 transition-all duration-300 ${
                          validationErrors.email
                            ? "border-red-300 focus:border-red-500 focus:ring-red-200 bg-red-50"
                            : focusedField === 'email'
                            ? "border-green-500 focus:border-green-500 focus:ring-green-200 bg-green-50"
                            : "border-gray-200 focus:border-green-500 focus:ring-green-200 bg-white"
                        }`}
                        placeholder="Enter your email address"
                      />
                      <Mail className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${
                        focusedField === 'email' ? 'text-green-500' : 'text-gray-400'
                      }`} />
                    </div>
                    {validationErrors.email && (
                      <div className="flex items-center space-x-2 text-red-500 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span>{validationErrors.email[0]}</span>
                      </div>
                    )}
                  </div>

                  <div className="space-y-3">
                    <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                      <PhoneCall className="w-4 h-4" />
                      <span>Phone Number</span>
                    </label>
                    <div className="relative">
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleChange}
                        onFocus={() => setFocusedField('phone')}
                        onBlur={() => setFocusedField('')}
                        className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 transition-all duration-300 ${
                          validationErrors.phone
                            ? "border-red-300 focus:border-red-500 focus:ring-red-200 bg-red-50"
                            : focusedField === 'phone'
                            ? "border-green-500 focus:border-green-500 focus:ring-green-200 bg-green-50"
                            : "border-gray-200 focus:border-green-500 focus:ring-green-200 bg-white"
                        }`}
                        placeholder="+1 (720) 362-9369"
                      />
                      <PhoneCall className={`absolute left-4 top-1/2 transform -translate-y-1/2 w-5 h-5 transition-colors duration-300 ${
                        focusedField === 'phone' ? 'text-green-500' : 'text-gray-400'
                      }`} />
                    </div>
                    {validationErrors.phone && (
                      <div className="flex items-center space-x-2 text-red-500 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span>{validationErrors.phone[0]}</span>
                      </div>
                    )}
                  </div>

                  {/* <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Required Quantity
                    </label>
                    <input
                      type="number"
                      name="quantity"
                      value={formData.quantity}
                      onChange={handleChange}
                      placeholder="Minimum 10,000 products"
                      min="10000"
                      className={`w-full px-4 py-3 rounded-xl border-2 ${validationErrors.quantity
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-green-300 focus:border-green-500 focus:ring-green-200"
                        } transition-all duration-300`}
                    />
                    {validationErrors.quantity && (
                      <p className="text-red-500 text-sm mt-1">
                        {validationErrors.quantity[0]}
                      </p>
                    )}
                  </div> */}

                  <div className="md:col-span-2 space-y-4">
                    <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                      <Package className="w-4 h-4" />
                      <span>Product Sizes & Quantities</span>
                    </label>
                    <div className="bg-gray-50 rounded-xl p-6 border border-gray-200">
                      {getSizeOptions()}
                    </div>
                    {validationErrors.sizes && (
                      <div className="flex items-center space-x-2 text-red-500 text-sm">
                        <AlertCircle className="w-4 h-4" />
                        <span>{validationErrors.sizes[0]}</span>
                      </div>
                    )}
                  </div>

                  <div className="md:col-span-2 space-y-4">
                    <div className="bg-gradient-to-r from-green-50 to-emerald-50 rounded-xl p-6 border border-green-200">
                      <div className="flex items-center space-x-3">
                      <input
                        type="checkbox"
                        id="isLogo"
                        name="isLogo"
                        checked={formData.isLogo}
                        onChange={handleChange}
                          className="h-5 w-5 text-green-600 focus:ring-green-500 border-gray-300 rounded"
                      />
                      <label
                        htmlFor="isLogo"
                          className="text-sm font-semibold text-gray-700 flex items-center space-x-2"
                      >
                          <Image className="w-4 h-4" />
                          <span>Logo Required</span>
                      </label>
                    </div>
                      <p className="text-xs text-gray-600 mt-2 ml-8">
                        Select if you need custom branding/logo on the product
                    </p>
                    </div>
                  </div>

                  {/* <div className="space-y-2">
                    <label className="text-sm font-medium text-gray-700">
                      Color Type
                    </label>
                    <select
                      name="colorRequirement"
                      value={formData.colorRequirement}
                      onChange={handleChange}
                      className="w-full px-4 py-3 rounded-xl border-2 border-green-300 focus:border-green-500 focus:ring-green-200 transition-all duration-300"
                    >
                      <option value="single">Single Color</option>
                      <option value="multiple">Multiple Colors</option>
                    </select>
                  </div> */}
                </div>

                {/* Color Selection and Logo Upload */}
                <div className="mt-8 grid grid-cols-1 lg:grid-cols-2 gap-8">
                  <div className="space-y-4">
                    <div className="bg-gradient-to-r from-blue-50 to-indigo-50 rounded-xl p-6 border border-blue-200">
                      <h4 className="font-semibold text-gray-700 flex items-center space-x-2 mb-4">
                        <Palette className="w-5 h-5" />
                        <span>Product Colors</span>
                      </h4>
                    {renderColorInputs()}
                    {validationErrors.productColors && (
                        <div className="flex items-center space-x-2 text-red-500 text-sm mt-3">
                          <AlertCircle className="w-4 h-4" />
                          <span>{validationErrors.productColors[0]}</span>
                        </div>
                      )}
                    </div>
                  </div>

                  {formData.isLogo && (
                    <div className="space-y-4">
                      <div className="bg-gradient-to-r from-purple-50 to-pink-50 rounded-xl p-6 border border-purple-200">
                        <h4 className="font-semibold text-gray-700 flex items-center space-x-2 mb-4">
                          <FileImage className="w-5 h-5" />
                          <span>Logo Upload</span>
                        </h4>
                        {renderLogoUpload()}
                      </div>
                    </div>
                  )}
                </div>

                <div className="mt-8 space-y-4">
                  <label className="text-sm font-semibold text-gray-700 flex items-center space-x-2">
                    <FileText className="w-4 h-4" />
                    <span>Project Details</span>
                  </label>
                  <div className="relative">
                  <textarea
                    name="message"
                    value={formData.message}
                    onChange={handleChange}
                      onFocus={() => setFocusedField('message')}
                      onBlur={() => setFocusedField('')}
                      rows="6"
                      className={`w-full pl-12 pr-4 py-4 rounded-xl border-2 transition-all duration-300 resize-none ${
                        validationErrors.message
                          ? "border-red-300 focus:border-red-500 focus:ring-red-200 bg-red-50"
                          : focusedField === 'message'
                          ? "border-green-500 focus:border-green-500 focus:ring-green-200 bg-green-50"
                          : "border-gray-200 focus:border-green-500 focus:ring-green-200 bg-white"
                      }`}
                      placeholder="Describe your project, specific needs, timeline, and any other relevant information..."
                    />
                    <FileText className={`absolute left-4 top-4 w-5 h-5 transition-colors duration-300 ${
                      focusedField === 'message' ? 'text-green-500' : 'text-gray-400'
                    }`} />
                  </div>
                  {validationErrors.message && (
                    <div className="flex items-center space-x-2 text-red-500 text-sm">
                      <AlertCircle className="w-4 h-4" />
                      <span>{validationErrors.message[0]}</span>
                    </div>
                  )}
                </div>

                <div className="mt-10">
                <button
                  type="submit"
                  disabled={loading}
                    className="group relative w-full bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 disabled:from-gray-400 disabled:to-gray-500 text-white py-5 px-8 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl disabled:scale-100 disabled:shadow-none overflow-hidden"
                  >
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
                    <div className="flex items-center justify-center space-x-3 relative z-10">
                      {loading ? (
                        <>
                          <RefreshCw className="w-6 h-6 animate-spin" />
                          <span>SENDING REQUEST...</span>
                        </>
                      ) : (
                        <>
                          <Send className="w-6 h-6" />
                          <span>SEND MY REQUEST</span>
                          <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                        </>
                      )}
                    </div>
                </button>
                  
                  <div className="mt-6 flex items-center justify-center space-x-6 text-sm text-gray-600">
                    <div className="flex items-center space-x-2">
                      <Shield className="w-4 h-4" />
                      <span>Secure Submission</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Leaf className="w-4 h-4" />
                      <span>Eco-Friendly</span>
                    </div>
                    <div className="flex items-center space-x-2">
                      <Award className="w-4 h-4" />
                      <span>Quality Guaranteed</span>
                    </div>
                  </div>
                </div>
              </form>
            </div>
          </div>
        ) : (
          <div className={`transition-all duration-1000 delay-600 ${isVisible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'} max-w-2xl mx-auto`}>
            <div className="bg-white/80 backdrop-blur-lg rounded-2xl shadow-xl border border-white/20 overflow-hidden">
              <div className="bg-gradient-to-r from-green-600 via-emerald-600 to-teal-600 py-8 px-8">
                <div className="flex items-center space-x-3">
                  <div className="w-12 h-12 bg-white/20 rounded-xl flex items-center justify-center">
                    <CheckCircle className="w-6 h-6 text-white" />
            </div>
                  <div>
                    <h2 className="text-3xl font-bold text-white">
              Request Sent Successfully!
                    </h2>
                    <p className="text-green-100 mt-1">
                      Your quote request has been submitted
                    </p>
                  </div>
                </div>
              </div>
              
              <div className="p-12 text-center">
                <div className="w-24 h-24 bg-gradient-to-r from-green-500 to-emerald-600 rounded-full flex items-center justify-center mx-auto mb-8 animate-pulse">
                  <CheckCircle className="w-14 h-14 text-white" />
                </div>
                
                <h3 className="text-3xl font-bold text-gray-900 mb-6">
                  Thank You for Your Request!
            </h3>
                
                <p className="text-lg text-gray-600 mb-8 max-w-lg mx-auto">
                  Our team will review your project details and contact you within 24 hours with a personalized quote.
                </p>
                
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
                  <div className="bg-gradient-to-r from-blue-50 to-blue-100 p-6 rounded-xl border border-blue-200">
                    <Clock className="w-8 h-8 text-blue-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-gray-900 mb-2">Quick Response</h4>
                    <p className="text-sm text-gray-600">We'll get back to you within 24 hours</p>
          </div>

                  <div className="bg-gradient-to-r from-green-50 to-green-100 p-6 rounded-xl border border-green-200">
                    <Shield className="w-8 h-8 text-green-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-gray-900 mb-2">Secure Process</h4>
                    <p className="text-sm text-gray-600">Your information is safe and secure</p>
                  </div>
                  
                  <div className="bg-gradient-to-r from-purple-50 to-purple-100 p-6 rounded-xl border border-purple-200">
                    <Award className="w-8 h-8 text-purple-600 mx-auto mb-3" />
                    <h4 className="font-semibold text-gray-900 mb-2">Quality Guaranteed</h4>
                    <p className="text-sm text-gray-600">Premium eco-friendly products</p>
                  </div>
                </div>
                
                <div className="flex flex-col sm:flex-row gap-4 justify-center">
                  <button className="group relative bg-gradient-to-r from-green-600 to-emerald-600 hover:from-green-700 hover:to-emerald-700 text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl overflow-hidden">
                    <div className="absolute inset-0 bg-gradient-to-r from-transparent via-white to-transparent opacity-0 group-hover:opacity-20 transform -skew-x-12 -translate-x-full group-hover:translate-x-full transition-all duration-700"></div>
                    <div className="flex items-center space-x-3 relative z-10">
                      <Heart className="w-5 h-5" />
                      <span>Explore Our Products</span>
                      <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform duration-300" />
                    </div>
                  </button>
                  
                  <button className="group relative bg-white border-2 border-green-600 text-green-600 hover:bg-green-600 hover:text-white px-8 py-4 rounded-xl font-semibold text-lg transition-all duration-300 transform hover:scale-105 hover:shadow-xl">
                    <div className="flex items-center space-x-3">
                      <PhoneCall className="w-5 h-5" />
                      <span>Contact Support</span>
                    </div>
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {/* Custom Styles */}
      <style jsx>{`
        .animation-delay-2000 {
          animation-delay: 2s;
        }

        .animation-delay-4000 {
          animation-delay: 4s;
        }
      `}</style>
    </div>
  );
};

export default DevisPage;
